<template>
  <div class="page-container">
    <div class="settings-form">
      <h2 class="page-title">文章列表管理</h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.article_name" placeholder="请输入文章标题" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <!-- 添加分类下拉框 -->
        <el-select v-model="searchForm.category_id" placeholder="选择分类" clearable size="large"
          style="width: 180px; margin-right: 15px" filterable>
          <el-option label="不选择" :value="null" />
          <el-option v-for="item in categories" :key="item.id" :label="item.article_category" :value="item.id" />
        </el-select>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>

        <el-button type="success" size="large" @click="openAddDialog" style="float: right">
          <el-icon>
            <Plus />
          </el-icon>
          新增文章
        </el-button>
      </div>

      <!-- 文章列表 -->
      <el-table :data="processedArticleList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
        <el-table-column prop="article_name" label="文章标题" width="300" />

        <el-table-column label="错误提示" width="180">
          <template #default="{ row }">
            <el-popover v-if="row.error_message" placement="top-start" width="300" trigger="hover">
              <template #reference>
                <el-tag type="danger" size="small" style="cursor: pointer">
                  <el-icon>
                    <Warning />
                  </el-icon>
                  查看错误
                </el-tag>
              </template>
              <div class="error-message-content">
                {{ row.error_message }}
              </div>
            </el-popover>
            <span v-else class="empty-value">无错误</span>
          </template>
        </el-table-column>


        <el-table-column prop="article_category" label="分类" width="150" />
        <el-table-column prop="use_num" label="使用次数" width="150" />
        <el-table-column prop="article_status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusTag(row.article_status)">
              {{ getStatusText(row.article_status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="tuku_name" label="图库" width="150" />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <!-- 在任务状态列后面新增重试列 -->
        <el-table-column label="重试" width="120" fixed="right">
          <template #default="{ row }">
            <el-button type="warning" size="small" :disabled="[1, 2, 3, 4].includes(row.article_status)"
              @click="handleRetry(row)">
              <el-icon>
                <Refresh />
              </el-icon>
              重试
            </el-button>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchArticleList" @current-change="fetchArticleList" />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="700px" :close-on-click-modal="false">
      <el-form ref="articleForm" :model="articleForm" label-width="120px" :rules="formRules">
        <el-form-item label="文章标题" prop="article_name">
          <el-input v-model="articleForm.article_name" placeholder="请输入文章标题" size="large" />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="文章分类" prop="article_category_id">
              <el-select v-model="articleForm.article_category_id" placeholder="请选择分类" style="width: 100%" filterable>
                <el-option v-for="category in categories" :key="category.id" :label="category.article_category"
                  :value="category.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="图库" prop="tuku_id">
              <el-select v-model="articleForm.tuku_id" placeholder="请选择图库" style="width: 100%" filterable>
                <el-option v-for="gallery in galleries" :key="gallery.id" :label="gallery.tuku_name"
                  :value="gallery.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="文章状态" prop="article_status">
              <el-radio-group v-model="articleForm.article_status" style="flex-wrap: nowrap;">
                <el-radio :label="1">待审核</el-radio>
                <el-radio :label="2">正常</el-radio>
                <el-radio :label="3">禁用</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="文章内容" prop="article_content">
          <RichTextEditor ref="richTextEditor" width="700px" v-model:content="articleForm.article_content"
            @error="handleEditorError" @show-html="showHtmlDialog" />

        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="articleForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitArticleForm">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete, Warning, Refresh
} from '@element-plus/icons-vue'
import RichTextEditor from '@/components/RichTextEditor.vue';

export default {
  name: 'ArticleList',
  components: { Search, Plus, Edit, Delete, RichTextEditor, Warning, Refresh },
  data() {
    return {
      loading: false,
      dialogVisible: false,
      dialogTitle: '新增文章',
      isEditMode: false,
      searchForm: {
        article_name: '',
        category_id: null
      },
      articleForm: {
        id: null,
        article_name: '',
        article_category_id: null,
        tuku_id: null,
        article_status: 1,
        article_content: '',
        notes: ''
      },
      formRules: {
        article_name: [
          { required: true, message: '请输入文章标题', trigger: 'blur' }
        ],
        article_category_id: [
          { required: true, message: '请选择文章分类', trigger: 'change' }
        ],
        tuku_id: [
          { required: true, message: '请选择图库', trigger: 'change' }
        ],
        article_content: [
          { required: true, message: '请输入文章内容', trigger: 'blur' }
        ]
      },
      articleList: [],
      categories: [],
      galleries: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },

    // 添加计算属性，将分类ID映射为分类名称
    categoryMap() {
      const map = {};
      this.categories.forEach(category => {
        map[category.id] = category.article_category;
      });
      return map;
    },

    // 添加计算属性，将图库ID映射为图库名称
    galleryMap() {
      const map = {};
      this.galleries.forEach(gallery => {
        map[gallery.id] = gallery.tuku_name;
      });
      return map;
    },

    // 处理后的文章列表，添加分类名称和图库名称
    processedArticleList() {
      return this.articleList.map(article => {
        return {
          ...article,
          article_category: this.categoryMap[article.article_category_id] || '未知分类',
          tuku_name: this.galleryMap[article.tuku_id] || '未知图库'
        };
      });
    }
  },
  mounted() {
    this.fetchArticleList();
    this.fetchCategories();
    this.fetchGalleries();
  },
  methods: {

    // 修改后的 handleRetry 方法
    async handleRetry(row) {
      // 前端先检查不允许重试的状态
      const disallowedStatuses = [1, 2, 3, 4]; // 待审核、正常、禁用状态不能重试
      if (disallowedStatuses.includes(row.article_status)) {
        this.$message.warning(`当前状态为"${this.getStatusText(row.article_status)}"，不能重试`);
        return;
      }

      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/article_list_management.php`,
          {
            action: 'retry',
            id: row.id,
            store_id: this.userInfo.id
          }
        );

        if (data.success) {
          this.$message.success('任务已重置为AI做图状态');
          this.fetchArticleList(); // 刷新列表
        } else {
          this.$message.error(data.error || '重试任务失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '重试任务失败';
        this.$message.error(`重试失败: ${errorMsg}`);
        console.error('重试错误:', error);
      }
    },

    // 完善后的状态标签颜色
    getStatusTag(status) {
      switch (status) {
        case 1: return 'info';      // 待审核 - 蓝色
        case 2: return 'success';   // 正常 - 绿色
        case 3: return 'danger';    // 禁用 - 红色
        case 4: return 'warning';   // AI做图 - 黄色
        case 5: return 'danger';    // 做图失败 - 红色
        case 6: return 'warning';   // AI做图 - 黄色
        default: return '';
      }
    },

    // 完善后的状态文本
    getStatusText(status) {
      switch (status) {
        case 1: return '待审核';
        case 2: return '正常';
        case 3: return '禁用';
        case 4: return 'AI做图中';
        case 5: return '做图失败';
        case 6: return '填充图片';
        default: return '未知';
      }
    },

    // 显示HTML代码弹窗
    showHtmlDialog(html) {
      this.$prompt('HTML代码', '查看HTML源码', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputType: 'textarea',
        inputValue: html,
        customClass: 'html-code-dialog',
        inputAttributes: {
          rows: 10,
          style: 'width: 100%; font-family: monospace;'
        }
      }).then(() => { }).catch(() => { });
    },

    // 处理富文本编辑器错误
    handleEditorError(message) {
      this.$message.error(message);
    },



    // 获取文章列表
    async fetchArticleList() {
      this.loading = true;
      try {
        const params = {
          action: 'search', // 统一使用search动作
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        // 如果有搜索条件，加入搜索参数
        if (this.searchForm.article_name) {
          params.article_name = this.searchForm.article_name;
        }
        if (this.searchForm.category_id) {
          params.article_category_id = this.searchForm.category_id;
        }
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/article_list_management.php`,
          params
        );

        if (data.data) {
          this.articleList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取文章列表失败');
        console.error('获取文章列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 获取分类列表
    async fetchCategories() {
      try {
        const params = {
          action: 'list_all',
          store_id: this.userInfo.id,
        };

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/article_category_management.php`,
          params
        );

        if (data.data) {
          this.categories = data.data;
        }
      } catch (error) {
        console.error('获取分类列表错误:', error);
      }
    },

    // 获取图库列表
    async fetchGalleries() {
      try {
        const params = {
          action: 'list_all',
          store_id: this.userInfo.id
        };

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/library_management.php`,
          params
        );

        if (data.data) {
          this.galleries = data.data;
        }
      } catch (error) {
        console.error('获取图库列表错误:', error);
      }
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchArticleList();
    },

    // 打开新增对话框
    openAddDialog() {
      this.dialogTitle = '新增文章';
      this.isEditMode = false;
      this.articleForm = {
        id: null,
        article_name: '',
        article_category_id: null,
        tuku_id: null,
        article_status: 2,
        article_content: '',
        notes: ''
      };

      // 添加关键代码：延迟确保编辑器内容重置
      setTimeout(() => {
        // 显式触发内容更新
        if (this.$refs.richTextEditor) {
          this.$refs.richTextEditor.clearContent(); // 调用富文本编辑器组件的清空方法
        }
      }, 100);
      this.dialogVisible = true;
    },

    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑文章';
      this.isEditMode = true;
      this.articleForm = {
        id: row.id,
        article_name: row.article_name,
        article_category_id: row.article_category_id,
        tuku_id: row.tuku_id,
        article_status: row.article_status,
        article_content: row.article_content || '', // 确保内容字段设置正确
        notes: row.notes || '',
      };

      // 添加关键代码
      setTimeout(() => {
        if (this.$refs.richTextEditor) {
          this.$refs.richTextEditor.setContent(row.article_content);
        }
      }, 100);
      this.dialogVisible = true;
    },

    // 提交表单（新增/编辑）
    submitArticleForm() {
      this.$refs.articleForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = {
              ...this.articleForm,
              store_id: this.userInfo.id
            };

            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/article_list_management.php`,
              {
                action,
                ...payload
              }
            );

            if (data.success) {
              this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchArticleList();
            } else {
              this.$message.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          }
        }
      });
    },

    // 删除文章
    async handleDelete(id) {
      this.$confirm('确定要删除该文章吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/article_list_management.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchArticleList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/page-common.scss' as *;
</style>